import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WithAuthenticatorProps } from '@aws-amplify/ui-react';
import { getUserIdentity, type UserIdentity } from '../lib/auth';
import { SessionHistory } from '../components/SessionHistory/SessionHistory';
import { StoryPlanWidget } from '../components/StoryPlanWidget/StoryPlanWidget';
import { getUnseenVersion } from '../lib/changelog';

type DashboardProps = Pick<WithAuthenticatorProps, 'signOut'>;

export function Dashboard({ signOut }: DashboardProps) {
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Read synchronously — no useState/useEffect. React Router remounts Dashboard on nav,
  // so this always reflects current localStorage state.
  const hasUnseenUpdate = !!getUnseenVersion();

  useEffect(() => {
    getUserIdentity()
      .then(setIdentity)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <main className="min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 space-y-6">
          <header className="flex items-center gap-4">
            <img src="/assets/logos/mlh_icon_transparent.svg" alt="My Life History" className="w-12 h-12 shrink-0" />
            <div>
              <h1 className="text-3xl font-serif text-stone-900">MyLife</h1>
              <p className="text-stone-600 mt-0.5">Your memoir starts here.</p>
            </div>
          </header>

          {error && (
            <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
              {error}
            </div>
          )}

          {/* Profile completion prompt — shown only when profile is missing */}
          <div id="profile-prompt-banner" style={{ display: 'none' }}>
            <div className="text-stone-600 bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm flex items-center justify-between gap-4">
              <span>Complete your profile so Claude can ask you personal questions.</span>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="text-amber-600 font-semibold hover:underline whitespace-nowrap"
              >
                Set Up My Profile →
              </button>
            </div>
          </div>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate('/session/new')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              Start New Session
            </button>
            <button
              type="button"
              onClick={() => navigate('/photos')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
              </svg>
              Photos
            </button>
            <button
              type="button"
              onClick={() => navigate('/book')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
              </svg>
              Read My Book
            </button>
            <button
              type="button"
              onClick={() => navigate('/curriculum')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path fillRule="evenodd" d="M2.625 6.75a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875 0A.75.75 0 018.25 6h12a.75.75 0 010 1.5h-12a.75.75 0 01-.75-.75zM2.625 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zM7.5 12a.75.75 0 01.75-.75h12a.75.75 0 010 1.5h-12A.75.75 0 017.5 12zm-4.875 5.25a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875 0a.75.75 0 01.75-.75h12a.75.75 0 010 1.5h-12a.75.75 0 01-.75-.75z" clipRule="evenodd" />
              </svg>
              Life Areas
            </button>
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
              </svg>
              Edit Profile
            </button>
          </div>

          {/* What's New changelog link */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/whats-new')}
              className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600 transition-colors"
            >
              {hasUnseenUpdate && (
                <span
                  className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"
                  aria-label="New update"
                />
              )}
              <span className={hasUnseenUpdate ? 'text-stone-500' : 'text-stone-400'}>
                What&apos;s New
              </span>
            </button>
          </div>

          <StoryPlanWidget />

          {identity && (identity.displayName ?? identity.email) && (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-stone-500">Signed in as</dt>
              {identity.displayName ? (
                <dd className="text-stone-900" data-testid="user-display-name">{identity.displayName}</dd>
              ) : (
                <dd className="font-mono text-stone-900" data-testid="user-email">{identity.email}</dd>
              )}
            </dl>
          )}

          <div className="pt-4 border-t border-stone-200">
            <button
              type="button"
              onClick={signOut}
              className="px-4 py-2 rounded-lg bg-stone-900 text-white hover:bg-stone-700 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Session history */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <SessionHistory />
        </div>
      </div>
      <p className="mt-6 text-xs text-stone-400">v{__APP_VERSION__}</p>
    </main>
  );
}
